import Constants from 'expo-constants';
import * as Device from 'expo-device';
import React, { useEffect } from 'react';
import { Platform } from 'react-native';

import { useSession } from '@/components/auth';
import { useAnalyticsConsent } from '@/hooks/use-analytics-consent';
import { useApi } from '@/hooks/use-api';
import { useAuth } from '@/hooks/use-auth';
import { useLocation } from '@/hooks/use-location';
import { autoCheckinService } from '@/services/auto-checkin-service';
import { locationService } from '@/services/location-service';

export const Tracker: React.FC = () => {
	const { location } = useLocation();
	const { fetchApi } = useApi();
	const { user } = useAuth();
	const { session } = useSession();
	const { isAllowed: analyticsAllowed, consent } = useAnalyticsConsent();
	const isDemoMode = session?.isDemoMode ?? false;

	// On iOS, wait for ATT to be resolved before starting location/auto-checkin so the
	// ATT prompt is the very first thing a new user sees (Apple guidance).
	const attResolved = consent !== 'unknown';

	function updateLocation() {
		// Demo mode is fully local — no analytics posts to the backend.
		if (!analyticsAllowed || !location || isDemoMode) return;
		const metadata = {
			devicePlatform: Platform.OS,
			deviceOS: Device.osName ? `${Device.osName}${Device.osVersion ? ` ${Device.osVersion}` : ''}` : Platform.OS,
			deviceModel: Device.modelName || (Platform.OS === 'web' ? 'Web Browser' : Platform.OS === 'ios' ? 'iOS Device' : 'Android Device'),
			appVersion: (Constants as any)?.expoConfig?.version || 'unknown',
			accuracy: location.accuracy ?? null,
			source: 'tracker',
		};
		fetchApi('/analytics', {
			method: 'POST',
			body: JSON.stringify({
				action: 'location_update',
				location: location,
				metadata,
			}),
		}).catch((error) => {
			// Swallow — fire-and-forget analytics should never bubble up as an
			// unhandled promise rejection, especially UnauthorizedError from
			// offline/local-only sessions.
			console.warn('Tracker: analytics update failed', error instanceof Error ? error.message : error);
		});
	}

	// Start auto check-in and location tracking services when user is authenticated.
	// On iOS, wait until ATT is resolved (consent !== 'unknown') so the ATT prompt
	// is the first thing a new user sees, per Apple guidance.
	useEffect(() => {
		let isMounted = true;
		// Demo sessions don't have a real backend; skip auto-checkin + location
		// tracking (both persist via the API) to avoid UnauthorizedError spam.
		const canStart = user && !isDemoMode && Platform.OS !== 'web' && (Platform.OS !== 'ios' || attResolved);

		const startServices = async () => {
			try {
				if (canStart && isMounted && user) {
					console.log('Starting services for user:', user.name);

					// Add a small delay to ensure user object is fully initialized
					await new Promise(resolve => setTimeout(resolve, 100));

					if (isMounted && user) {
						// Start both auto-checkin and location tracking services
						autoCheckinService.startMonitoring(user);
						await locationService.initialize(user);
					}
				}
			} catch (error) {
				console.error('Error starting services:', error);
			}
		};

		startServices();

		// Cleanup on unmount or user change
		return () => {
			isMounted = false;
			console.log('Stopping services');
			try {
				autoCheckinService.stopMonitoring();
				locationService.cleanup();
			} catch (error) {
				console.error('Error stopping services:', error);
			}
		};
	}, [user, attResolved, isDemoMode]);

	useEffect(() => {
		if (location) {
			updateLocation();
			const timeout = setTimeout(() => {
				updateLocation();
			}, 10000);
			return () => clearTimeout(timeout);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps -- updateLocation is inline function; adding it would cause re-creation every render
	}, [location, analyticsAllowed]);

	return null;
};
